import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { staffApi, timeSince } from "./staffApi";

const Section = ({ title, children, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-white/10 bg-[#141419] p-4">
    <p className="text-xs font-black tracking-[0.25em] text-[#D4AF37]">{title}</p>
    {children}
  </div>
);

export default function ManagerArea({ mgr, onExit, onOpenOrder }) {
  const [roster, setRoster] = useState([]);
  const [members, setMembers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [closeout, setCloseout] = useState(null);
  const [audit, setAudit] = useState([]);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [role, setRole] = useState("waiter");
  const [cp, setCp] = useState(null);
  const [regen, setRegen] = useState(null); // {actorPin}
  const [newPin, setNewPin] = useState(null); // one-time display of a regenerated owner PIN
  const [rename, setRename] = useState({});
  const isOwner = mgr.role === "owner";
  const idleRef = useRef(null);

  const bump = useCallback(() => { // 60s inactivity auto-locks the management area
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(onExit, 60000);
  }, [onExit]);
  useEffect(() => {
    bump();
    const evs = ["pointerdown", "keydown"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => { clearTimeout(idleRef.current); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, [bump]);

  const load = useCallback(() => {
    staffApi.get("/staff/roster").then((r) => setRoster(r.data.rows || [])).catch(() => {});
    staffApi.get("/staff/members").then((r) => setMembers(r.data)).catch(() => {});
    staffApi.get("/staff/closeout").then((r) => setCloseout(r.data)).catch(() => {});
    staffApi.get("/staff/audit").then((r) => setAudit(r.data.rows || [])).catch(() => {});
    if (isOwner) staffApi.get("/staff/devices").then((r) => setDevices(r.data)).catch(() => {});
  }, [isOwner]);
  useEffect(load, [load]);

  const err = (e, fb) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fb);

  const endTheirShift = (r) =>
    staffApi.post(`/staff/roster/${r.staff_id}/end`)
      .then((res) => { toast.success(`${r.name} off shift — ${res.data.released} table(s) released to the floor`); load(); })
      .catch((e) => err(e, "Could not end their shift"));

  const closeFloor = async () => {
    if (!window.confirm("End every remaining shift and close the floor? Service items must already be clear.")) return;
    try {
      const r = await staffApi.post("/staff/closeout/complete");
      toast.success(`Floor closed — ${r.data.shifts_ended} remaining shift(s) ended`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.message) toast.error(d.message); else err(e, "Closeout blocked");
      load();
    }
  };

  const addMember = () =>
    staffApi.post("/staff/members", { name, pin, role })
      .then(() => { toast.success(`${name} added`); setName(""); setPin(""); setRole("waiter"); load(); })
      .catch((e) => err(e, "Could not add"));

  const changePin = (m) =>
    staffApi.post(`/staff/members/${m.staff_id}/change-pin`, { actor_pin: cp.actorPin, new_pin: cp.newPin })
      .then(() => { setCp(null); toast.success(`${m.name}'s PIN reset — their sessions are signed out`); load(); })
      .catch((e) => err(e, "Could not reset PIN"));

  const doRegen = () =>
    staffApi.post(`/staff/members/${mgr.staff_id}/regenerate-pin`, { actor_pin: regen.actorPin })
      .then((r) => {
        sessionStorage.setItem("ut_staff_token", r.data.token); // this screen stays signed in; every other device is out
        setNewPin(r.data.pin);
        setRegen(null);
        load();
      })
      .catch((e) => err(e, "Could not regenerate"));

  const revoke = (d) => {
    if (!window.confirm(`Revoke "${d.name || d.label}"? That device loses the open floor screens until a PIN is entered on it again.`)) return;
    staffApi.post(`/staff/devices/${d.device_id}/revoke`).then(() => { toast.success("Device revoked"); load(); }).catch((e) => err(e, "Could not revoke"));
  };
  const saveName = (d) => {
    const v = (rename[d.device_id] || "").trim();
    if (v.length < 2) return;
    staffApi.post(`/staff/devices/${d.device_id}/rename`, { name: v }).then(() => { toast.success("Device named"); load(); }).catch((e) => err(e, "Could not rename"));
  };

  const co = closeout;
  const coItems = co ? [...co.unclaimed.map((o) => ({ ...o, why: "Unclaimed order" })),
    ...co.active_visits.map((o) => ({ ...o, why: `Active visit — ${o.assigned_name || "?"}` })),
    ...co.pending_transfers.map((o) => ({ ...o, why: `Transfer waiting for ${o.to_name}` }))] : [];

  return (
    <div data-testid="manager-area" className="fixed inset-0 z-[60] bg-[#0D0D10] overflow-y-auto">
      <div className="max-w-2xl mx-auto p-4 pb-20 space-y-4">
        <div className="flex items-center justify-between sticky top-0 bg-[#0D0D10] py-3 z-10">
          <p className="text-lg font-black tracking-wide">{isOwner ? "OWNER" : "MANAGER"} TOOLS <span className="text-white/40 text-sm">· {mgr.name} · locks after 60s idle</span></p>
          <button data-testid="manager-exit" onClick={onExit} aria-label="Exit"
            className="w-10 h-10 rounded-full border border-white/20 text-white/60 flex items-center justify-center"><X size={16} /></button>
        </div>

        <Section title="FLOOR CLOSEOUT — SERVICE ONLY, MONEY CLOSES IN THE POS" testId="floor-closeout">
          {!co ? <p className="text-white/40 mt-2">Checking the floor…</p> : (
            <>
              <div className="mt-2 text-sm font-bold text-white/70 space-y-1">
                <p data-testid="closeout-counts">Unclaimed: {co.unclaimed.length} · Active visits: {co.active_visits.length} · Pending transfers: {co.pending_transfers.length} · On shift: {co.on_shift.length} · Sync errors: {co.sync_errors.failed + co.sync_errors.stale_pending}</p>
              </div>
              {coItems.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {coItems.map((o, i) => (
                    <button key={`${o.order_id}-${i}`} data-testid={`closeout-item-${o.order_id}`}
                      onClick={() => { onExit(); onOpenOrder(o.order_id); }}
                      className="w-full text-left rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-bold hover:border-[#D4AF37]/40">
                      T{o.table} · {o.order_id} — {o.why} <span className="text-white/40">(tap to open & resolve)</span>
                    </button>
                  ))}
                </div>
              )}
              {(co.sync_errors.failed > 0 || co.sync_errors.stale_pending > 0) && (
                <p className="mt-2 text-sm font-bold text-[#FF5A4E]">Resolve POS sync from the Board's red banner before closing.</p>
              )}
              <button data-testid="closeout-complete" onClick={closeFloor} disabled={!co.clear}
                className="w-full mt-3 rounded-xl bg-[#D4AF37] text-black font-black py-3.5 tracking-wide disabled:opacity-40">
                END REMAINING SHIFTS AND CLOSE FLOOR
              </button>
              {!co.clear && <p className="text-[11px] text-white/40 font-bold mt-1.5">Blocked until every service item above is resolved — nothing closes silently.</p>}
            </>
          )}
        </Section>

        <Section title="ON SHIFT NOW" testId="mgr-roster">
          {roster.length === 0 && <p className="text-white/40 mt-2">Nobody on shift.</p>}
          <div className="divide-y divide-white/8 mt-1">
            {roster.map((r) => (
              <div key={r.staff_id} className="py-3 flex items-center justify-between gap-2">
                <div>
                  <p className="text-base font-bold">{r.name}</p>
                  <p className="text-xs text-white/45 font-bold uppercase">{r.role === "waiter" ? "VeerGi" : r.role} · on since {new Date(r.on_since).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
                </div>
                <button data-testid={`mgr-end-${r.staff_id}`} onClick={() => endTheirShift(r)}
                  className="text-xs font-black text-[#F0B429] border border-[#F0B429]/40 rounded-full px-4 py-2.5">END SHIFT & RELEASE TABLES</button>
              </div>
            ))}
          </div>
        </Section>

        <Section title="TEAM" testId="mgr-team">
          {newPin && (
            <div data-testid="regen-pin-reveal" className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center px-6">
              <div className="w-full max-w-sm rounded-3xl border border-[#D4AF37]/50 bg-[#141419] p-6 text-center">
                <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black">YOUR NEW OWNER PIN — SHOWN ONCE</p>
                <p data-testid="regen-pin-value" className="font-display text-5xl font-black tracking-[0.3em] my-5">{newPin}</p>
                <p className="text-xs text-white/55">Write it down now — it will not be shown again, anywhere. Every other device has been signed out; this screen stays signed in.</p>
                <button data-testid="regen-pin-done" onClick={() => setNewPin(null)}
                  className="mt-5 w-full bg-[#D4AF37] text-black font-black rounded-full py-3 text-xs tracking-widest">I'VE WRITTEN IT DOWN</button>
              </div>
            </div>
          )}
          <div className="divide-y divide-white/8 mt-1">
            {members.map((m) => {
              const canPin = isOwner ? m.role !== "owner" || m.staff_id === mgr.staff_id
                : m.role === "waiter" || m.staff_id === mgr.staff_id;
              return (
                <div key={m.staff_id} data-testid={`mgr-member-${m.staff_id}`} className="py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-base font-bold">{m.name} {!m.active && <span className="text-[#FF5A4E] text-xs font-black">· DEACTIVATED</span>}</p>
                      <p className="text-xs text-white/45 font-bold uppercase">{m.role === "waiter" ? "VeerGi" : m.role}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {canPin && (
                        <button data-testid={`mgr-resetpin-${m.staff_id}`}
                          onClick={() => { setCp(cp?.staff_id === m.staff_id ? null : { staff_id: m.staff_id, actorPin: "", newPin: "" }); setRegen(null); }}
                          className="text-xs font-black text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-4 py-2.5">RESET PIN</button>
                      )}
                      {isOwner && m.active && m.role === "owner" && m.staff_id === mgr.staff_id && (
                        <button data-testid="regen-pin-btn"
                          onClick={() => { setRegen(regen ? null : { actorPin: "" }); setCp(null); }}
                          className="text-xs font-black text-[#FF9A3E] border border-[#FF9A3E]/40 rounded-full px-4 py-2.5">REGENERATE PIN</button>
                      )}
                      {isOwner && m.active && m.staff_id !== mgr.staff_id && (
                        <button data-testid={`mgr-deactivate-${m.staff_id}`}
                          onClick={() => staffApi.post(`/staff/members/${m.staff_id}/deactivate`).then(() => { toast.success("Deactivated"); load(); }).catch((e) => err(e, "Failed"))}
                          className="text-xs font-black text-[#FF5A4E] border border-[#FF5A4E]/40 rounded-full px-4 py-2.5">DEACTIVATE</button>
                      )}
                      {isOwner && !m.active && (
                        <button data-testid={`mgr-reactivate-${m.staff_id}`}
                          onClick={() => staffApi.post(`/staff/members/${m.staff_id}/reactivate`).then(() => { toast.success("Reactivated"); load(); }).catch((e) => err(e, "Failed"))}
                          className="text-xs font-black text-[#2ECC71] border border-[#2ECC71]/40 rounded-full px-4 py-2.5">REACTIVATE</button>
                      )}
                    </div>
                  </div>
                  {cp?.staff_id === m.staff_id && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-xl border border-[#D4AF37]/25 bg-[#16130A] p-3">
                      <input data-testid="resetpin-actor" type="password" inputMode="numeric" autoComplete="off" placeholder="YOUR current PIN"
                        value={cp.actorPin} onChange={(e) => setCp({ ...cp, actorPin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base" />
                      <input data-testid="resetpin-new" type="password" inputMode="numeric" autoComplete="off" placeholder={`NEW PIN for ${m.name}`}
                        value={cp.newPin} onChange={(e) => setCp({ ...cp, newPin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base" />
                      <button data-testid="resetpin-save" disabled={cp.actorPin.length < 4 || cp.newPin.length < 4} onClick={() => changePin(m)}
                        className="rounded-xl bg-[#D4AF37] text-black font-black py-3 text-sm disabled:opacity-40">SAVE</button>
                      <p className="sm:col-span-3 text-[11px] text-white/40 font-bold">The old PIN is never shown to anyone — PINs are hashed on the server.</p>
                    </div>
                  )}
                  {regen && m.staff_id === mgr.staff_id && (
                    <div data-testid="regen-pin-form" className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-[#FF9A3E]/30 bg-[#17120A] p-3">
                      <input data-testid="regen-actor-pin" type="password" inputMode="numeric" autoComplete="off" placeholder="YOUR current PIN"
                        value={regen.actorPin} onChange={(e) => setRegen({ actorPin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base" />
                      <div className="flex gap-2">
                        <button data-testid="regen-pin-confirm" disabled={regen.actorPin.length < 4} onClick={doRegen}
                          className="flex-1 rounded-xl bg-[#FF9A3E] text-black font-black py-3 text-sm disabled:opacity-40">GENERATE NEW PIN</button>
                        <button data-testid="regen-pin-cancel" onClick={() => setRegen(null)}
                          className="rounded-xl border border-white/20 text-white/70 font-black py-3 px-4 text-sm">CANCEL</button>
                      </div>
                      <p className="sm:col-span-2 text-[11px] text-white/40 font-bold">A random 6-digit PIN is generated for you and shown ONCE on this screen. Every other signed-in device is signed out; this screen stays in.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {isOwner && (
            <>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input data-testid="mgr-add-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
                  className="sm:col-span-2 bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base" />
                <input data-testid="mgr-add-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="PIN (4-6)"
                  inputMode="numeric" className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base" />
                <select data-testid="mgr-add-role" value={role} onChange={(e) => setRole(e.target.value)}
                  className="bg-[#1A1A20] border border-white/15 rounded-xl px-3 py-3 text-base">
                  <option value="waiter">VeerGi (server)</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
              <button data-testid="mgr-add-btn" onClick={addMember} disabled={name.trim().length < 2 || pin.length < 4}
                className="w-full mt-2 rounded-xl bg-[#D4AF37] text-black font-black py-3.5 tracking-wide disabled:opacity-40">ADD EMPLOYEE</button>
            </>
          )}
          {!isOwner && <p data-testid="mgr-team-note" className="mt-3 text-xs text-white/45 font-bold">Adding, deactivating and role changes are owner-only. You can reset a VeerGi's PIN.</p>}
        </Section>

        {isOwner && (
          <Section title="TRUSTED FLOOR DEVICES — OWNER ONLY" testId="mgr-devices">
            <p className="text-[11px] text-white/40 font-bold mt-1">Name each device so you know exactly what you're revoking — Front Counter Tablet, Floor Tablet, Kitchen Screen…</p>
            <div className="divide-y divide-white/8 mt-1">
              {devices.filter((d) => !d.revoked_at).map((d) => (
                <div key={d.device_id} data-testid={`mgr-device-${d.device_id}`} className="py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate">{d.name || d.label || d.device_id}</p>
                      <p className="text-xs text-white/40 font-bold truncate">{d.label}{d.agent ? ` · ${d.agent.slice(0, 40)}` : ""} · added {new Date(d.created_at).toLocaleDateString()}{d.last_seen ? ` · seen ${timeSince(d.last_seen)}` : ""}</p>
                    </div>
                    <button data-testid={`mgr-revoke-${d.device_id}`} onClick={() => revoke(d)}
                      className="shrink-0 text-xs font-black text-[#FF5A4E] border border-[#FF5A4E]/40 rounded-full px-4 py-2.5">REVOKE</button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <input data-testid={`mgr-devname-${d.device_id}`} value={rename[d.device_id] ?? ""} placeholder="Name this device…"
                      onChange={(e) => setRename({ ...rename, [d.device_id]: e.target.value })}
                      className="flex-1 bg-[#1A1A20] border border-white/15 rounded-lg px-3 py-2 text-sm" />
                    <button data-testid={`mgr-devname-save-${d.device_id}`} onClick={() => saveName(d)}
                      className="rounded-lg border border-[#D4AF37]/50 text-[#D4AF37] px-4 text-xs font-black">SAVE</button>
                  </div>
                </div>
              ))}
              {devices.filter((d) => !d.revoked_at).length === 0 && <p className="text-white/40 mt-2">No trusted devices.</p>}
            </div>
          </Section>
        )}

        <Section title="AUDIT — LATEST 50" testId="mgr-audit">
          <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto">
            {audit.map((a, i) => (
              <p key={i} className="text-xs text-white/55 font-semibold">
                {String(a.ts).slice(5, 16).replace("T", " ")} · <b className="text-white/80">{a.name || a.actor_name || "system"}</b> {a.event || a.action}
                {a.detail ? ` — ${a.detail}` : ""}{a.reason ? ` (${a.reason})` : ""}
              </p>
            ))}
            {audit.length === 0 && <p className="text-white/40">No audit records yet.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}
